import { Engine } from 'engine/Engine';
import { Entity } from 'engine/Entity';
import { ColliderFacet, ColliderType } from 'engine/facets/ColliderFacet';
import { Facet } from 'engine/facets/Facet';
import { KinematicBodyFacet } from 'engine/facets/KinematicBodyFacet';
import { StaticSpriteFacet } from 'engine/facets/StaticSpriteFacet';
import { AudioManager } from 'engine/lib/AudioManager';
import { create } from 'engine/lib/create';
import { Programmable } from 'game/behaviors/Programmable';
import { CollisionLayer } from 'game/config';
import { BotState } from 'game/state/BotState';
import { DoorBehavior } from '../bot/DoorBehavior';
import { LabWallDoorSprite } from './LabWallSprite';

const closeSrc = require('game/sfx/metal-close.mp3');
const openSrc = require('game/sfx/metal-open.mp3');
const slideSrc = require('game/sfx/metal-slide.mp3');
const scanSrc = require('game/sfx/scan.mp3');

interface LabWallDoorProps {
  state: BotState,
}

export class ScannerBehavior extends Facet {

  scanStart: number | null = null;
  scanDuration = 1200;
  scanSound = new AudioManager(scanSrc);

  update(delta: number, engine: Engine) {
    if (this.scanStart !== null) {
      if (engine.time.now >= this.scanStart + this.scanDuration) this.scanStart = null;
    }
  }

  scan(engine: Engine) {
    if (this.scanSound.playing) {
      return;
    }
    this.scanSound.play();
    this.scanStart = engine.time.now;
  }
}

class LabWallDoorBehavior extends Facet<LabWallDoorProps> {

  programmable = create(Programmable, this.props);
  initialX = 0;
  lastX = 0;
  lastMoving = false;

  openAudio = new AudioManager(openSrc, { volume: 0.25 });
  closeAudio = new AudioManager(closeSrc, { volume: 0.25 });
  slideAudio = new AudioManager(slideSrc, { loop: true });

  start(engine: Engine) {
    this.props.state.position = this.entity.transform.position;
    this.initialX = this.entity.transform.position[0];
    this.lastX = this.initialX;
    this.entity.addFacet(this.programmable, engine);
    this.entity.addFacet(create(DoorBehavior, {
      range: 180,
      speed: this.props.state.botSpeed,
      entity: this.entity,
    }), engine);
  }

  stop() {
    this.slideAudio.stop();
  }

  update() {
    const [x] = this.entity.transform.position;
    const moving = x !== this.lastX;
    if (moving) {
      this.slideAudio.play();
    } else {
      this.slideAudio.pause();
    }
    if (!moving && this.lastMoving) {
      this.closeAudio.play();
    } else if (moving && !this.lastMoving) {
      this.openAudio.play();
    }
    this.lastX = x;
    this.lastMoving = moving;
  }

  physicsUpdate(delta: number, engine: Engine) {
    this.programmable.step(delta, engine);
    const { result } = this.programmable;
    if (result?.status === 'running' || result?.status === 'success') {
      if (result.value.type === 'command') {
        return;
      }
    }
    this.entity.getFacet(DoorBehavior)?.close(delta);
  }
}

export class LabWallDoorEntity extends Entity<LabWallDoorProps> {
  createFacets(props: LabWallDoorProps) {
    return [
      create(StaticSpriteFacet, { frame: LabWallDoorSprite, offset: [0, -390] }),
      create(ColliderFacet, { type: ColliderType.Box,
        size: [220, 40],
        offset: [0, -40],
        layer: CollisionLayer.Door,
      }),
      create(ScannerBehavior, {}),
      create(LabWallDoorBehavior, props),
      create(KinematicBodyFacet, { constrainX: [0, 180], constrainY: [0, 0] }),
    ];
  }
}
